'use client'

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import clsx from 'clsx'
import Icon from 'lib/components/Icon/Icon'
import { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import {
  MutableRefObject,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Controller } from 'react-hook-form'
import InputAddon, { InputAddonProps } from '../InputAddon/InputAddon'
import InputError from '../InputError/InputError'
import InputHint from '../InputHint/InputHint'
import InputLabel from '../InputLabel/InputLabel'

export interface SelectInputOption {
  value: string | number
  label: ReactNode
  disabled?: boolean
}

type SelectInputStandaloneProps = InputAddonProps &
  Omit<FormFieldCommonProps, 'onChange'> & {
    options: SelectInputOption[]
    /**
     * Placeholder text to show when no option is selected yet
     */
    placeholder?: ReactNode
    /**
     * Optional classnames for specified children element
     */
    childrenClassNames?: Partial<
      Record<'ListboxButton' | 'ListboxOptions', string>
    >
    onChange?: (data?: string | number, fieldName?: string) => void
  }

export type SelectInputProps = ControlledFormField & SelectInputStandaloneProps

// Standalone component that can be used outside react-hook-form's context
export const SelectInputStandalone = forwardRef(
  function SelectInputStandaloneWithRef(
    {
      name,
      label = '',
      options,
      defaultValue,
      placeholder,
      error,
      onChange,
      disabled = false,
      className,
      hint,
      childrenClassNames,
      ...props
    }: SelectInputStandaloneProps,
    ref,
  ) {
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const [isEnabled, setIsEnabled] = useState(
      'conditionalEdit' in props && props.conditionalEdit ? false : !disabled,
    )
    const [defaultValueCached, setDefaultValueCached] = useState<
      SelectInputOption['value'] | undefined
    >(defaultValue)
    const [selected, setSelected] = useState<
      SelectInputOption['value'] | undefined
    >(options.find(({ value }) => value === defaultValueCached)?.value)

    const onSelect = useCallback(
      (selected: string) => {
        if (selected) {
          setSelected(selected)
          onChange?.(selected, name)
        }
      },
      [onChange, name],
    )

    // Handles defaultValue change from the outside
    useEffect(() => {
      if (defaultValue !== defaultValueCached && options?.length) {
        if (inputRef.current) {
          inputRef.current.value = defaultValue as string
        }

        setDefaultValueCached(defaultValue)
        setSelected(defaultValue)
      }
    }, [defaultValue, defaultValueCached, options])

    useEffect(() => {
      const wrapperElem = wrapperRef.current
      if (!wrapperElem) return
      const observer = new MutationObserver(() => {
        const options = wrapperElem.querySelector(
          '.listboxoptions',
        ) as HTMLDivElement
        if (!options) return
        options.removeAttribute('inert')
        if (options.getAttribute('data-headlessui-state') === 'open') {
          if (
            options.offsetHeight >
            document.documentElement.clientHeight -
              wrapperElem.getBoundingClientRect().bottom
          ) {
            options.style.setProperty(
              'max-height',
              `${
                document.documentElement.clientHeight -
                wrapperElem.getBoundingClientRect().bottom
              }px`,
            )
          }
        } else {
          options.style.setProperty('max-height', '')
        }
      })
      observer.observe(wrapperElem, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['data-headlessui-state'],
      })
      return () => observer.disconnect()
    }, [wrapperRef])

    // Whenever the parent’s disabled changes, update our local state
    useEffect(() => {
      // Only force-sync if we aren’t in "conditionalEdit" mode
      if (!('conditionalEdit' in props && props.conditionalEdit)) {
        setIsEnabled(!disabled)
      }
    }, [disabled, props, props.conditionalEdit])

    return (
      <div
        className={clsx('flex flex-wrap relative w-full', className)}
        ref={wrapperRef}
      >
        <div className="flex items-center justify-between w-full">
          {label && <InputLabel className="w-full">{label}</InputLabel>}
          <InputAddon
            {...props}
            {...('conditionalEdit' in props && props.conditionalEdit
              ? { onEdit: setIsEnabled }
              : {})}
          />
        </div>
        <div className="relative w-full mt-2 mb-2">
          <Listbox
            defaultValue={selected}
            onChange={onSelect}
            disabled={!isEnabled}
          >
            <ListboxButton
              className={clsx(
                'group flex justify-start items-center h-12 px-4 rounded-8 w-full bg-white type-body-8',
                'lg:h-14',
                'data-[hover]:shadow-input-active',
                'disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-600 outline-neutral-700 data-[focus]:outline-accent-link',
                selected
                  ? 'text-neutral-700 shadow-input'
                  : 'text-neutral-600 shadow-input',
                error && '!shadow-input-error',
                childrenClassNames?.ListboxButton,
              )}
              aria-label={props['aria-label']}
              disabled={disabled || !isEnabled}
              ref={ref as MutableRefObject<HTMLButtonElement>}
            >
              <span className="inline-block truncate">
                {options.find(({ value }) => value === selected)?.label ||
                  placeholder ||
                  ''}
              </span>
              <Icon
                icon="chevron-down"
                className="w-6 h-6 ml-auto group-data-[open]:rotate-180 text-black"
              />
            </ListboxButton>
            <ListboxOptions
              anchor={{ to: 'bottom' }}
              className={clsx(
                'listboxoptions',
                // z-[400] keeps the popover above the Modal container (which
                // is z-[300]). Headless UI v2's `anchor` portals ListboxOptions
                // to document.body, so the popover leaves the modal's stacking
                // context — a lower z-index (was z-10) rendered behind the
                // signup modal's "Where did you hear about us?" select.
                'translate-y-2 border border-neutral-400 rounded-8 shadow-dropdown bg-white w-[var(--button-width)] max-h-[280px] overflow-auto z-[400] outline-neutral-700 data-[focus]:outline-accent-link',
                childrenClassNames?.ListboxOptions,
              )}
            >
              {options.map((option) => (
                <ListboxOption
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled ?? false}
                  className={clsx(
                    'flex items-center justify-start px-4 min-h-14 type-body-8 hover:bg-neutral-200',
                    defaultValue === option.value
                      ? 'data-[focus]:bg-neutral-200'
                      : '',
                    option.disabled
                      ? 'text-neutral-600 bg-neutral-200 cursor-not-allowed'
                      : '',
                  )}
                >
                  <span className="truncate">{option.label}</span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        </div>
        <InputHint>{hint}</InputHint>
        <InputError>{error}</InputError>
      </div>
    )
  },
)

// Controlled form element that is automatically registered to react-hook-form
export default function SelectInput({
  name,
  label = '',
  control,
  disabled = false,
  className = '',
  options,
  placeholder,
  rules = {},
  onChange,
  defaultValue,
  shouldUnregister,
  ...props
}: SelectInputProps) {
  return (
    <Controller
      name={name || ''}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      shouldUnregister={shouldUnregister}
      render={({
        field: { onChange: fieldOnChange, ...field },
        fieldState,
      }) => {
        const onInputChange = (data?: number | string) => {
          fieldOnChange?.(data)
          onChange?.(data, name)
        }
        return (
          <SelectInputStandalone
            {...{
              label,
              options,
              className,
              disabled,
              placeholder,
              rules,
            }}
            {...field}
            {...props}
            defaultValue={defaultValue}
            onChange={onInputChange}
            error={fieldState.error?.message}
          />
        )
      }}
    />
  )
}
